import { useState, useEffect, useMemo } from "react";
import { getCampaigns } from "../../../services/notification";
import { formatCampaignTitleCase } from "../campaignFormatUtils";

export const getCampaignStatus = (campaign) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const start = new Date(campaign.start);
  start.setHours(0, 0, 0, 0);
  const stop = new Date(campaign.stop);
  stop.setHours(23, 59, 59, 999);

  if (start > now) return "Scheduled";
  if (stop < now) return "Completed";
  return "InProgress";
};

/** Quai(x) + nom(s) de flux séparés, comme Emplacement / flux en historique. */
export const getCampaignZoneDisplay = (campaign) => {
  if (!campaign?.containers || campaign.containers.length === 0) {
    return { zoneLabel: "", flowLabel: "" };
  }

  const positionings = [];
  const flowNames = [];
  const seenFlows = new Set();

  campaign.containers.forEach((container) => {
    const positioning = formatCampaignTitleCase(container.positioning || "");
    const wasteName = formatCampaignTitleCase(container.wasteName || "");
    if (positioning) {
      positionings.push(positioning);
    }
    if (wasteName) {
      const key = wasteName.toLowerCase();
      if (!seenFlows.has(key)) {
        seenFlows.add(key);
        flowNames.push(wasteName);
      }
    }
  });

  return {
    zoneLabel: positionings.join(", "),
    flowLabel: flowNames.join(", "),
  };
};

/** @deprecated Prefer getCampaignZoneDisplay — kept for search/legacy joins. */
export const getContainerInfos = (campaign) => {
  const { zoneLabel, flowLabel } = getCampaignZoneDisplay(campaign);
  if (zoneLabel && flowLabel) {
    return [`${zoneLabel} (${flowLabel})`];
  }
  return [zoneLabel || flowLabel].filter(Boolean);
};

export const useCampaignListData = (periodStart, periodStop) => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const [filteredResponse, plannedResponse] = await Promise.all([
        getCampaigns({ start: periodStart, stop: periodStop }),
        // Scheduled-only merge: skip heavy Influx/Mongo metrics (column shows "-" anyway).
        getCampaigns({ start: now.toISOString(), skipMetrics: true }),
      ]);

      const filteredCampaigns = filteredResponse.data.campaigns || [];
      const plannedCampaigns = plannedResponse.data.campaigns || [];

      const seen = new Set(filteredCampaigns.map((c) => c._id));
      const merged = [
        ...filteredCampaigns,
        ...plannedCampaigns.filter((c) => !seen.has(c._id)),
      ];

      setCampaigns(merged);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (periodStart) {
      fetchCampaigns();
    }
    // eslint-disable-next-line
  }, [periodStart, periodStop]);

  const enrichedCampaigns = useMemo(
    () =>
      campaigns.map((campaign) => {
        const zoneDisplay = getCampaignZoneDisplay(campaign);
        return {
          ...campaign,
          status: getCampaignStatus(campaign),
          containerInfos: getContainerInfos(campaign),
          zoneLabel: zoneDisplay.zoneLabel,
          flowLabel: zoneDisplay.flowLabel,
          addressName: campaign.address?.name || "-",
          addressCity: campaign.address?.city?.trim() || "",
          startDate: new Date(campaign.start),
          stopDate: new Date(campaign.stop),
        };
      }),
    [campaigns],
  );

  return {
    campaigns: enrichedCampaigns,
    isLoading,
    fetchCampaigns,
  };
};
